import React, { useState, useEffect } from 'react';
import { adminPost, adminPatch, adminGet } from '../api';
import ImageInput from './ImageInput';

const DEFAULTS = {
  genre_id: '',
  title: '',
  description: '',
  link: '',
  thumbnail: '',
  is_recently_added: false,
  sort_order: 0,
};

/**
 * MovieForm — create or edit a movie.
 *
 * Props:
 *   movie    {object|null}
 *   onSave   {(movie) => void}
 *   onCancel {() => void}
 */
export default function MovieForm({ movie = null, onSave, onCancel }) {
  const isEdit = Boolean(movie);
  const [form, setForm]     = useState({ ...DEFAULTS, ...(movie || {}) });
  const [genres, setGenres] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  useEffect(() => {
    adminGet('/api/admin/movie-genres')
      .then(setGenres)
      .catch(() => { /* genres will stay empty; user can still type genre_id */ });
  }, []);

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const payload = {
      genre_id: Number(form.genre_id),
      title: form.title.trim(),
      description: form.description?.trim() || null,
      link: form.link.trim(),
      thumbnail: form.thumbnail || null,
      is_recently_added: Boolean(form.is_recently_added),
      sort_order: Number(form.sort_order ?? 0),
    };
    setSaving(true);
    try {
      const result = isEdit
        ? await adminPatch(`/api/admin/movies/${movie.id}`, payload)
        : await adminPost('/api/admin/movies', payload);
      onSave(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="admin-form" onSubmit={handleSubmit}>
      <h3 style={{ margin: '0 0 20px', fontSize: 16, color: '#1a1a2e' }}>
        {isEdit ? `Edit: ${movie.title}` : 'Add movie'}
      </h3>

      {error && <div className="admin-banner admin-banner-error">{error}</div>}

      <div className="admin-form-row">
        <label>Genre</label>
        <select value={form.genre_id} onChange={e => set('genre_id', e.target.value)} required>
          <option value="">— select genre —</option>
          {genres.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
      </div>

      <div className="admin-form-row">
        <label>Title</label>
        <input type="text" value={form.title} onChange={e => set('title', e.target.value)} required />
      </div>

      <div className="admin-form-row">
        <label>Description</label>
        <textarea rows={3} value={form.description || ''} onChange={e => set('description', e.target.value)} />
      </div>

      <div className="admin-form-row">
        <label>Link (URL)</label>
        <input type="url" value={form.link} onChange={e => set('link', e.target.value)} required />
        <p style={{ margin: '4px 0 0', fontSize: 11, color: '#888', lineHeight: 1.4 }}>
          Supports: YouTube (youtube.com/watch, youtu.be, Shorts), Vimeo, Archive.org download URLs, or any direct .mp4
        </p>
      </div>

      <div className="admin-form-row">
        <ImageInput
          value={form.thumbnail}
          onChange={v => set('thumbnail', v)}
          label="Thumbnail"
        />
      </div>

      <div className="admin-form-row">
        <label>Sort order</label>
        <input type="number" value={form.sort_order} onChange={e => set('sort_order', e.target.value)} />
      </div>

      <div className="admin-form-row">
        <label className="admin-checkbox-row" style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>
          <input
            type="checkbox"
            checked={Boolean(form.is_recently_added)}
            onChange={e => set('is_recently_added', e.target.checked)}
          />
          Recently added
        </label>
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add movie'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
